/**
 * usePlayerGestures Hook
 *
 * Custom hook for handling gesture-based player interactions on mobile
 *
 * Features:
 * - Swipe down to minimize fullscreen player
 * - Swipe up to expand minimized player
 * - Swipe left/right to change chapters
 * - Drag to scrub timeline
 * - Natural physics-based animations
 */

import { useDrag } from "@use-gesture/react";
import { useState, useCallback } from "react";
import {
	shouldMinimize,
	shouldExpand,
	clamp,
	hapticFeedback,
} from "@/lib/utils/gesture.utils";

interface UsePlayerGesturesOptions {
	onMinimize?: () => void;
	onExpand?: () => void;
	onNextChapter?: () => void;
	onPrevChapter?: () => void;
	onSeek?: (progress: number) => void;
	isFullscreen?: boolean;
	disabled?: boolean;
}

interface GestureState {
	// Current drag offset
	y: number;
	x: number;
	// Whether the gesture is active
	isDragging: boolean;
	// Current opacity for fade effect
	opacity: number;
}

export function usePlayerGestures(options: UsePlayerGesturesOptions) {
	const {
		onMinimize,
		onExpand,
		onNextChapter,
		onPrevChapter,
		disabled = false,
		isFullscreen = true,
	} = options;

	const [gestureState, setGestureState] = useState<GestureState>({
		y: 0,
		x: 0,
		isDragging: false,
		opacity: 1,
	});

	// Vertical drag gesture (for minimize/expand)
	const bindVerticalDrag = useDrag(
		({
			active,
			movement: [, my],
			velocity: [, vy],
			direction: [, dy],
			cancel,
		}) => {
			if (disabled) return;

			// Only allow downward swipes in fullscreen mode
			if (isFullscreen && dy > 0 && my > 0) {
				// Calculate opacity based on drag distance (fade out as user drags)
				const opacity = clamp(1 - my / 400, 0.3, 1);

				setGestureState({
					y: my,
					x: 0,
					isDragging: active,
					opacity,
				});

				// Check if should minimize
				if (!active && shouldMinimize(my, vy)) {
					hapticFeedback("light");
					onMinimize?.();
					cancel();
				}
			}
			// Only allow upward swipes in minimized mode
			else if (!isFullscreen && dy < 0 && my < 0) {
				const absY = Math.abs(my);
				const opacity = clamp(0.3 + absY / 400, 0.3, 1);

				setGestureState({
					y: my,
					x: 0,
					isDragging: active,
					opacity,
				});

				// Check if should expand
				if (!active && shouldExpand(absY, Math.abs(vy))) {
					hapticFeedback("light");
					onExpand?.();
					cancel();
				}
			}

			// Reset state when gesture ends
			if (!active) {
				setGestureState({
					y: 0,
					x: 0,
					isDragging: false,
					opacity: 1,
				});
			}
		},
		{
			filterTaps: true,
			axis: "y",
			rubberband: true,
		},
	);

	// Horizontal drag gesture (for chapter navigation)
	const bindHorizontalDrag = useDrag(
		({ active, movement: [mx], velocity: [vx], direction: [dx], cancel }) => {
			if (disabled) return;

			const SWIPE_THRESHOLD = 100;
			const VELOCITY_THRESHOLD = 0.5;

			setGestureState((prev) => ({
				...prev,
				x: mx,
				isDragging: active,
			}));

			if (!active) {
				// Swipe left - next chapter
				if (
					dx < 0 &&
					(Math.abs(mx) > SWIPE_THRESHOLD || Math.abs(vx) > VELOCITY_THRESHOLD)
				) {
					hapticFeedback("medium");
					onNextChapter?.();
					cancel();
				}
				// Swipe right - previous chapter
				else if (
					dx > 0 &&
					(Math.abs(mx) > SWIPE_THRESHOLD || Math.abs(vx) > VELOCITY_THRESHOLD)
				) {
					hapticFeedback("medium");
					onPrevChapter?.();
					cancel();
				}

				setGestureState((prev) => ({
					...prev,
					x: 0,
					isDragging: false,
				}));
			}
		},
		{
			filterTaps: true,
			axis: "x",
			rubberband: true,
		},
	);

	// Combined gesture for artwork area (both vertical and horizontal)
	const bindArtworkDrag = useDrag(
		({
			active,
			movement: [mx, my],
			velocity: [vx, vy],
			direction: [dx, dy],
			cancel,
			canceled,
		}) => {
			if (disabled) return;

			// If gesture was canceled (action triggered), reset immediately
			if (canceled) {
				setGestureState({
					y: 0,
					x: 0,
					isDragging: false,
					opacity: 1,
				});
				return;
			}

			// Determine primary direction
			const isVertical = Math.abs(my) > Math.abs(mx);

			if (isVertical) {
				// Handle vertical gestures
				if (isFullscreen && dy > 0 && my > 0) {
					const opacity = clamp(1 - my / 400, 0.3, 1);

					setGestureState({
						y: my,
						x: 0,
						isDragging: active,
						opacity,
					});

					if (!active && shouldMinimize(my, vy)) {
						hapticFeedback("light");
						setGestureState({
							y: 0,
							x: 0,
							isDragging: false,
							opacity: 1,
						});
						onMinimize?.();
						cancel();
						return;
					}
				}
			} else {
				// Handle horizontal gestures (chapter navigation)
				const SWIPE_THRESHOLD = 100;
				const VELOCITY_THRESHOLD = 0.5;

				setGestureState((prev) => ({
					...prev,
					x: mx,
					isDragging: active,
				}));

				if (!active) {
					if (
						dx < 0 &&
						(Math.abs(mx) > SWIPE_THRESHOLD ||
							Math.abs(vx) > VELOCITY_THRESHOLD)
					) {
						hapticFeedback("medium");
						setGestureState({
							y: 0,
							x: 0,
							isDragging: false,
							opacity: 1,
						});
						onNextChapter?.();
						cancel();
						return;
					} else if (
						dx > 0 &&
						(Math.abs(mx) > SWIPE_THRESHOLD ||
							Math.abs(vx) > VELOCITY_THRESHOLD)
					) {
						hapticFeedback("medium");
						setGestureState({
							y: 0,
							x: 0,
							isDragging: false,
							opacity: 1,
						});
						onPrevChapter?.();
						cancel();
						return;
					}
				}
			}

			// Reset state when gesture ends
			if (!active) {
				setGestureState({
					y: 0,
					x: 0,
					isDragging: false,
					opacity: 1,
				});
			}
		},
		{
			filterTaps: true,
			rubberband: true,
		},
	);

	// Reset gesture state
	const reset = useCallback(() => {
		setGestureState({
			y: 0,
			x: 0,
			isDragging: false,
			opacity: 1,
		});
	}, []);

	return {
		gestureState,
		bindVerticalDrag,
		bindHorizontalDrag,
		bindArtworkDrag,
		reset,
	};
}
